import React, { useEffect } from 'react';
import { StepReviewsProps, ReviewProp } from './types';
import {
  loadSearchActions,
  loadSearchAnalyticsActions,
  ResultList,
  SearchEngine,
  loadAdvancedSearchQueryActions
} from '@coveo/headless';
import { ExtendedWindowType, headlessEngineModal } from '../../engine';
import {
  resultListModal,
  fieldsObjectModal
} from '../../controllers/controller';
import ResultListContractor from './result-list-contractor';
import { CoveoContextModal } from '../context';
import ContractorCheckbox from './contractor';

const StepReviews: React.FC<StepReviewsProps> = ({
  headingReviews,
  copyReviews,
  reviewName,
  formData,
  setFormData,
  contractorId,
  showDefaultContractor,
  defaultContractor,
  defaultContractorCopy,
  defaultLabel,
  disclaimer,
  disclaimerHeading,
  setContractorSelected,
  isModal,
  certificationsCardLabel,
  viewAllCertificationsLabel,
  certificationsModalId,
  termsConditionsName,
  checkbox,
  copy
}) => {
  const { latitude, longitude } = formData;

  useEffect(() => {
    if (latitude && longitude) {
      const global = window as ExtendedWindowType;

      global.modalPreProcessRequest = (body: string) => {
        const newBody = JSON.parse(body.toString());
        newBody.queryFunctions = [
          {
            fieldName: `@${fieldsObjectModal.distanceInMiles}`,
            function: `dist(@${fieldsObjectModal.latitudeField}, @${fieldsObjectModal.longitudeField}, ${latitude}, ${longitude})*0.000621371`
          }
        ];
        return JSON.stringify(newBody);
      };
    }

    const headlessEngine = headlessEngineModal as SearchEngine;

    let defaultCountry = 'USA';

    if (fieldsObjectModal.defaultCountry) {
      defaultCountry = fieldsObjectModal.defaultCountry;
    }

    const advancedQuerySearchActionCreator =
      loadAdvancedSearchQueryActions(headlessEngine);
    headlessEngine?.dispatch(
      advancedQuerySearchActionCreator.registerAdvancedSearchQueries({
        aq: `@${fieldsObjectModal.distanceInMiles} <= ${fieldsObjectModal.maxDistance} AND @${fieldsObjectModal.country} = ${defaultCountry}`
      })
    );

    const { logInterfaceLoad } = loadSearchAnalyticsActions(headlessEngine);
    const { executeSearch } = loadSearchActions(headlessEngine);
    headlessEngine.dispatch(executeSearch(logInterfaceLoad()));
  }, [formData?.zipCode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    contractorReview: ReviewProp
  ) => {
    const value = e.target.checked;
    if (value) {
      setFormData(prevFormData => {
        if (prevFormData)
          return {
            ...prevFormData,
            [reviewName]: [
              ...(prevFormData[reviewName] as string[]),
              contractorReview.id
            ]
          };
        return prevFormData;
      });
    } else {
      setFormData(prevFormData => {
        if (prevFormData)
          return {
            ...prevFormData,
            [reviewName]: (prevFormData[reviewName] as string[]).filter(
              (contractor: string) => contractor !== contractorReview.id
            )
          };
        return prevFormData;
      });
    }
  };

  useEffect(() => {
    setContractorSelected(
      (formData.reviews && (formData.reviews as string[]).length > 0) as boolean
    );
  }, [formData]);

  return (
    <div className="request-a-quote__content">
      <CoveoContextModal />
      {showDefaultContractor === 'true' && defaultContractor && !isModal && (
        <>
          <p className="request-a-quote__default-copy">
            {defaultContractorCopy}
          </p>
          <p className="request-a-quote__default-label">{defaultLabel}</p>
          <ContractorCheckbox
            formData={formData}
            handleChange={handleChange}
            review={{
              name: reviewName,
              contractorDBA: defaultContractor.contractorDBA,
              contractorName: defaultContractor.contractorName,
              city: defaultContractor.city,
              state: defaultContractor.state,
              rating: defaultContractor.rating,
              numberOfReviews: defaultContractor.numberOfReviews,
              id: defaultContractor.id
            }}
            certifications={{
              title: "",
              certificates: [],
              viewAllCertificationsLabel: viewAllCertificationsLabel
            }}
            modalId={certificationsModalId}
          />
        </>
      )}
      <h3
        className={`request-a-quote__card-heading ${
          isModal ? 'request-a-quote__card-heading--modal' : ''
        }`}
      >
        {headingReviews}
      </h3>
      {isModal ? (
        <>
          <p className="request-a-quote__default-label request-a-quote__default-label--modal">
            {defaultLabel}
          </p>
          <div className="request-a-quote__contractors-grid">
            {showDefaultContractor === 'true' && defaultContractor && (
              <ContractorCheckbox
                formData={formData}
                handleChange={handleChange}
                certifications={{
                  title: "",
                  certificates: [],
                  viewAllCertificationsLabel: viewAllCertificationsLabel
                }}
                review={{
                  name: reviewName,
                  contractorDBA: defaultContractor.contractorDBA,
                  contractorName: defaultContractor.contractorName,
                  city: defaultContractor.city,
                  state: defaultContractor.state,
                  rating: defaultContractor.rating,
                  numberOfReviews: defaultContractor.numberOfReviews,
                  id: defaultContractor.id
                }}
                modalId={certificationsModalId}
              ></ContractorCheckbox>
            )}
            <ResultListContractor
              formData={formData}
              reviewName={reviewName}
              handleChange={handleChange}
              controller={resultListModal as ResultList}
              copy={copyReviews}
              contractorId={contractorId ? contractorId : ''}
              certificationsCardLabel={certificationsCardLabel}
              viewAllCertificationsLabel={viewAllCertificationsLabel}
              certificationsModalId={certificationsModalId}
            ></ResultListContractor>
          </div>
        </>
      ) : (
        <ResultListContractor
          formData={formData}
          reviewName={reviewName}
          handleChange={handleChange}
          controller={resultListModal as ResultList}
          copy={copyReviews}
          contractorId={contractorId ? contractorId : ''}
          certificationsCardLabel={certificationsCardLabel}
          viewAllCertificationsLabel={viewAllCertificationsLabel}
          certificationsModalId={certificationsModalId}
        ></ResultListContractor>
      )}
      {!isModal && (
        <p className="request-a-quote__disclaimer-heading">
          {disclaimerHeading}
        </p>
      )}
      <div
        className={`request-a-quote__disclaimer rtf ${
          isModal ? 'request-a-quote__disclaimer--modal' : ''
        }`}
        dangerouslySetInnerHTML={{
          __html: disclaimer
        }}
      ></div>

      <div
        className={
          isModal ? 'request-a-quote__content-terms-wrapper' : undefined
        }
      >
        <div className="request-a-quote__content-terms-checkbox field field--checkbox">
          <input
            name={termsConditionsName}
            className="field__checkbox-input field__control"
            type="checkbox"
            id="request-a-quote-form-id-checkbox-terms"
            checked={formData[termsConditionsName] as boolean}
            required={true}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFormData(prevFormData => ({
                ...prevFormData,
                [termsConditionsName]: e.target.checked
              }));
            }}
          />
          <div className="field__checkbox-label-wrapper">
            <label
              className="field__checkbox-label"
              htmlFor="request-a-quote-form-id-checkbox-terms"
            >
              {checkbox.label}
            </label>
          </div>
        </div>
        <p
          className="request-a-quote__content-copy rtf"
          dangerouslySetInnerHTML={{ __html: copy }}
        ></p>
      </div>

    </div>
  );
};

export default StepReviews;
